import {
  type AdminEntity,
  type GetApiAdminCompaniesResponse,
  usePatchAdminCompanyEntity,
} from '@sit/client-shared';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableCheckbox,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import Icon from 'carbon-react/lib/components/icon';
import { isSyncEnabled } from '../helpers';

interface CompanyEntitiesTableProps {
  entities: AdminEntity[];
}

const headDataItems: { id: HeaderItemId; name: string; info?: string }[] = [
  {
    id: 'id',
    name: 'SIT ID',
  },
  {
    id: 'name',
    name: 'Name',
  },
  {
    id: 'lastAccessed',
    name: 'Last accessed',
  },
  {
    id: 'lastSynced',
    name: 'Last synced',
  },
  {
    id: 'isCREEnabled',
    name: 'CRE enabled',
  },
  {
    id: 'isEmbeddedEnabled',
    name: 'Embedded enabled',
  },
  {
    id: 'deletedOnIntacct',
    name: 'Deleted on Intacct',
  },
  {
    id: 'shouldSyncAutomatically',
    name: 'Auto sync enabled',
    info: 'Needs to be a production tenant type and accessed or created in the last two weeks to sync automatically.',
  },
  {
    id: 'actions',
    name: 'Actions',
  },
];

type HeaderItemId =
  | keyof GetApiAdminCompaniesResponse[number]['entities'][number]
  | 'actions';

export function CompanyEntitiesTable({ entities }: CompanyEntitiesTableProps) {
  const { mutate: updateEntity } = usePatchAdminCompanyEntity();

  const handleUpdateEntity = (id: string) => {
    updateEntity({
      pathParams: {
        id,
      },
      body: {
        shouldSyncAutomatically: true,
      },
    });
  };

  return (
    <>
      <Box width="100%">
        <FlatTable
          mt={2}
          colorTheme="transparent-white"
          hasStickyFooter
          hasStickyHead
          overflowX="auto"
          width="100%"
        >
          <FlatTableHead>
            <FlatTableRow>
              {headDataItems.map((dataItem) => (
                <FlatTableHeader key={dataItem.id}>
                  {dataItem.name}
                  {dataItem.info && (
                    <Icon
                      tooltipMessage={dataItem.info}
                      tooltipPosition="right"
                      type="help"
                    />
                  )}
                </FlatTableHeader>
              ))}
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {entities.map((entity) => (
              <FlatTableRow key={entity.id}>
                <FlatTableCell>{entity.id}</FlatTableCell>
                <FlatTableCell>{entity.name}</FlatTableCell>
                <FlatTableCell>{entity.lastAccessed}</FlatTableCell>
                <FlatTableCell>{entity.lastSynced}</FlatTableCell>
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={entity.isCREEnabled}
                />
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={entity.isEmbeddedEnabled}
                />
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={entity.deletedOnIntacct}
                />
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={entity.shouldSyncAutomatically}
                />
                <FlatTableCell>
                  {!isSyncEnabled(entity) && (
                    <Button
                      size="small"
                      onClick={() => handleUpdateEntity(entity.id)}
                    >
                      Re-enable sync
                    </Button>
                  )}
                </FlatTableCell>
              </FlatTableRow>
            ))}
          </FlatTableBody>
        </FlatTable>
      </Box>
    </>
  );
}
